import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as entitlementsApi from '../api/entitlements';

export function PlanEntitlements() {
  const { selectedOrg } = useOrg();
  const { hasPermission } = usePermissions();
  const canAdmin = hasPermission('admin');
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    if (!selectedOrg) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await entitlementsApi.getEntitlements(selectedOrg.id);
      setEntitlements(result.entitlements);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleFeature = async (feature, enabled) => {
    if (!selectedOrg || !canAdmin || saving) return;
    setSaving(true);
    try {
      const updated = await entitlementsApi.updateEntitlements(selectedOrg.id, {
        features: { [feature]: enabled }
      });
      setEntitlements(updated);
    } catch (err) {
      alert('Failed to update: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getPlanColor = (plan) => {
    switch (plan) {
      case 'enterprise': return { bg: '#e9d8fd', color: '#6b46c1' };
      case 'standard': return { bg: '#bee3f8', color: '#2b6cb0' };
      case 'starter': return { bg: '#e2e8f0', color: '#4a5568' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading plan details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  if (!entitlements) {
    return (
      <div style={styles.container}>
        <PageHeader
          title="Plan & Entitlements"
          subtitle="Manage your organization's plan and features"
        />
        <div style={styles.notConfigured}>
          <h3>No Plan Configured</h3>
          <p>Contact support to set up your organization's plan.</p>
        </div>
      </div>
    );
  }

  const planStyle = getPlanColor(entitlements.plan);
  const featureList = Object.entries(entitlements.features);

  return (
    <div style={styles.container}>
      <PageHeader
        title="Plan & Entitlements"
        subtitle="Manage your organization's plan and features"
      />

      <div style={styles.planHeader} data-testid="plan-header">
        <div style={styles.planBadge}>
          <span style={{ ...styles.planName, background: planStyle.bg, color: planStyle.color }}>
            {entitlements.plan}
          </span>
          <span style={styles.validUntil}>Valid until {entitlements.validUntil}</span>
        </div>
      </div>

      <div style={styles.grid}>
        <div style={styles.card}>
          <h4 style={styles.cardTitle}>Plan Limits</h4>
          <div style={styles.limitRow}>
            <span style={styles.limitLabel}>Max Users</span>
            <span style={styles.limitValue}>{entitlements.limits.maxUsers}</span>
          </div>
          <div style={styles.limitRow}>
            <span style={styles.limitLabel}>Max Assets</span>
            <span style={styles.limitValue}>{entitlements.limits.maxAssets}</span>
          </div>
          <div style={styles.limitRow}>
            <span style={styles.limitLabel}>Reports/Month</span>
            <span style={styles.limitValue}>{entitlements.limits.maxReportsPerMonth}</span>
          </div>
          <div style={styles.limitRow}>
            <span style={styles.limitLabel}>Exports/Month</span>
            <span style={styles.limitValue}>{entitlements.limits.maxExportsPerMonth}</span>
          </div>
          <div style={styles.limitRow}>
            <span style={styles.limitLabel}>Data Retention</span>
            <span style={styles.limitValue}>{entitlements.limits.retentionDays} days</span>
          </div>
        </div>

        <div style={styles.card}>
          <h4 style={styles.cardTitle}>Features</h4>
          <div style={styles.featuresList} data-testid="features-list">
            {featureList.map(([feature, enabled]) => (
              <div key={feature} style={styles.featureRow} data-testid={`feature-${feature}`}>
                <span style={styles.featureName}>{formatFeatureName(feature)}</span>
                {canAdmin ? (
                  <button
                    onClick={() => handleToggleFeature(feature, !enabled)}
                    disabled={saving}
                    style={enabled ? styles.enabledToggle : styles.disabledToggle}
                    data-testid={`toggle-${feature}`}
                  >
                    {enabled ? 'Enabled' : 'Disabled'}
                  </button>
                ) : (
                  <span style={enabled ? styles.enabledBadge : styles.disabledBadge}>
                    {enabled ? 'Enabled' : 'Disabled'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={styles.infoSection}>
        <p style={styles.infoText}>
          To upgrade your plan or adjust limits, please contact your account manager.
        </p>
      </div>
    </div>
  );
}

function formatFeatureName(feature) {
  return feature
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

const styles = {
  container: { padding: '2rem', maxWidth: '1000px', margin: '0 auto' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px' },
  notConfigured: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', color: '#718096' },
  planHeader: { marginBottom: '1.5rem' },
  planBadge: { display: 'flex', alignItems: 'center', gap: '1rem' },
  planName: { padding: '0.5rem 1rem', borderRadius: '9999px', fontSize: '1rem', fontWeight: '600', textTransform: 'capitalize' },
  validUntil: { fontSize: '0.875rem', color: '#718096' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' },
  card: { background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  cardTitle: { margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600', color: '#2d3748' },
  limitRow: { display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f7fafc' },
  limitLabel: { fontSize: '0.875rem', color: '#718096' },
  limitValue: { fontSize: '0.875rem', fontWeight: '500', color: '#2d3748' },
  featuresList: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  featureRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #f7fafc' },
  featureName: { fontSize: '0.875rem', color: '#2d3748' },
  enabledToggle: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #38a169', background: '#c6f6d5', color: '#276749', fontSize: '0.75rem', cursor: 'pointer' },
  disabledToggle: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0', background: '#f7fafc', color: '#718096', fontSize: '0.75rem', cursor: 'pointer' },
  enabledBadge: { padding: '0.25rem 0.5rem', borderRadius: '4px', background: '#c6f6d5', color: '#276749', fontSize: '0.75rem' },
  disabledBadge: { padding: '0.25rem 0.5rem', borderRadius: '4px', background: '#f7fafc', color: '#718096', fontSize: '0.75rem' },
  infoSection: { background: '#f7fafc', padding: '1rem', borderRadius: '8px' },
  infoText: { margin: 0, fontSize: '0.875rem', color: '#718096', textAlign: 'center' }
};
